/** Provides crop, rotation, a shared selection (rectangle, magic wand) and selection-scoped erase and adjustment tools for an image. */
import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import {Cropper, CropperRef} from 'react-advanced-cropper';
import {AppBar, Box, IconButton, Slider, ToggleButton, ToggleButtonGroup, Toolbar, Typography} from "@mui/material";
import Container from "@mui/material/Container";
import {
   AutoFixHigh,
   Brush,
   Contrast,
   Crop,
   CropFree,
   DeleteSweep,
   Deselect,
   FlipToBack,
   HighlightAlt,
   LightMode,
   RotateLeft,
   RotateRight,
   Undo
} from "@mui/icons-material";
import {AdjustableCropperBackground} from "./AdjustableCropperBackground";
import Button from "@mui/material/Button";
import {CropperState} from "advanced-cropper/types";
import {getZoomFactor} from 'advanced-cropper/extensions/absolute-zoom';
import {DocumentViewProps, ImagePointerHandlers} from './AdjustableImage';
import {AdjustmentKind} from './editOperations';
import {isTextEntryElement, mapImageBoundsToCropper, Point} from './imageEditing';
import {SelectionMode, selectionModeFromModifiers} from './selectionMask';
import {SelectionOverlayStyle} from './selectionOverlay';
import {useImageDocument} from './useImageDocument';

export interface ImageEditorProps {
   imageUrl: string;
   onSave: (image: Blob) => Promise<void>
}

/** The editor's mutually exclusive tools; Crop leaves the mouse to the cropper, the others edit pixels or the selection. */
enum EditorTool {
   Crop = 'crop',
   RectangleSelect = 'rectangleSelect',
   MagicWand = 'magicWand',
   EraseBrush = 'eraseBrush',
   Brightness = 'brightness',
   Contrast = 'contrast',
}

const TOOL_BUTTONS: { tool: EditorTool, title: string, icon: ReactNode }[] = [
   {tool: EditorTool.Crop, title: 'Crop', icon: <Crop/>},
   {tool: EditorTool.RectangleSelect, title: 'Rectangle select', icon: <HighlightAlt/>},
   {tool: EditorTool.MagicWand, title: 'Magic wand', icon: <AutoFixHigh/>},
   {tool: EditorTool.EraseBrush, title: 'Erase brush', icon: <Brush/>},
   {tool: EditorTool.Brightness, title: 'Brightness', icon: <LightMode/>},
   {tool: EditorTool.Contrast, title: 'Contrast', icon: <Contrast/>},
];

const SELECTION_HINT = 'Shift = add · Alt = subtract';

/** Screen distance in pixels below which a rectangle drag counts as a click. */
const CLICK_DISTANCE = 3;

/** Where a rectangle-select drag started, on the image and on screen. */
interface RectangleDrag {
   from: Point;
   clientX: number;
   clientY: number;
   mode: SelectionMode;
}

/** Renders the shared operator-facing image editor and saves its canvas output. */
export const ImageEditor = ({imageUrl, onSave}: ImageEditorProps) => {
   const cropperRef = useRef<CropperRef>(null)
   const [src] = useState(
      imageUrl
   );
   const [tool, setTool] = useState(EditorTool.Crop);
   const [brushSize, setBrushSize] = useState(5);
   const rectangleDragRef = useRef<RectangleDrag | null>(null);
   const {imageDocument, loadFailed, loadImage, failLoad} = useImageDocument();
   const ready = imageDocument.isReady() && !loadFailed;
   const selectionCommandsEnabled = ready && imageDocument.hasSelection();

   useEffect(() => {
      /** Maps editor shortcuts; Escape is deliberately unused because the hosting dialogs close on it. */
      const handleKeyDown = (event: KeyboardEvent) => {
         if (isTextEntryElement(event.target)) {
            return;
         }
         const key = event.key.toLowerCase();
         const withModifier = event.ctrlKey || event.metaKey;
         if (withModifier && key === 'z') {
            event.preventDefault();
            imageDocument.undo();
         } else if (withModifier && key === 'd') {
            event.preventDefault();
            imageDocument.deselect();
         } else if (!withModifier && (key === 'delete' || key === 'backspace')) {
            event.preventDefault();
            imageDocument.eraseSelection();
         }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
   }, [imageDocument]);

   const rotate = (angle: number) => {
      if (cropperRef.current) {
         const cropper = cropperRef.current;

         cropper.rotateImage(angle);

         cropper.zoomImage({
            factor: getZoomFactor(cropper.getState(), cropper.getSettings(), 0.0)
         })

         const image = cropper.getImage();
         cropper.setCoordinates({
            top: 0,
            left: 0,
            width: (image?.width || 1000) * 2,
            height: (image?.height || 1000) * 2
         })
      }
   };

   /** Commits pending edits, exports the cropped canvas as a PNG and hands it to the host. */
   const onImageSave = async () => {
      const cropper = cropperRef.current;
      if (cropper) {
         // Render a pending adjustment onto the export canvas before the cropper reads it.
         flushSync(() => imageDocument.commitPending());
         const canvas = cropper.getCanvas();
         if (!canvas) {
            return;
         }
         canvas.toBlob(async (blob) => {
            if (blob) {
               await onSave(blob);
            }
         });
         cropper.zoomImage(0)
      }
   };

   const defaultSize = ({imageSize, visibleArea}: CropperState) => {
      return {
         width: (visibleArea || imageSize).width,
         height: (visibleArea || imageSize).height,
      };
   }

   /** Switches tool, first committing a pending adjustment and ending tolerance tuning of the latest wand click. */
   const changeTool = (newTool: EditorTool) => {
      imageDocument.commitPending();
      setTool(newTool);
   }

   /** Snaps the crop box to the selection's bounding box and hands over to the Crop tool for fine-tuning. */
   const cropToSelection = () => {
      const cropper = cropperRef.current;
      const bounds = imageDocument.getSelectionBounds();
      const pixels = imageDocument.getPixels();
      if (!cropper || !bounds || !pixels) {
         return;
      }
      const rotation = cropper.getState()?.transforms.rotate ?? 0;
      cropper.setCoordinates(mapImageBoundsToCropper(bounds, pixels.width, pixels.height, rotation));
      changeTool(EditorTool.Crop);
   }

   /** Rectangle select: drag to select, plain click to deselect; the screen start tells clicks from drags. */
   const rectangleSelectHandlers: ImagePointerHandlers = {
      onPointerDown: event => {
         rectangleDragRef.current = {
            from: event.point,
            clientX: event.clientX,
            clientY: event.clientY,
            mode: selectionModeFromModifiers(event.shiftKey, event.altKey)
         };
      },
      onPointerMove: event => {
         const drag = rectangleDragRef.current;
         if (drag) {
            imageDocument.previewRectangle(drag.from, event.point, drag.mode);
         }
      },
      onPointerUp: event => {
         const drag = rectangleDragRef.current;
         rectangleDragRef.current = null;
         if (!drag) {
            return;
         }
         if (Math.hypot(event.clientX - drag.clientX, event.clientY - drag.clientY) >= CLICK_DISTANCE) {
            imageDocument.selectRectangle(drag.from, event.point, drag.mode);
         } else if (drag.mode === SelectionMode.Replace) {
            imageDocument.deselect();
         } else {
            imageDocument.cancelSelectionPreview();
         }
      }
   };

   /** Returns the active tool's pointer handlers, or undefined when the tool leaves the mouse to the cropper. */
   const getPointerHandlers = (): ImagePointerHandlers | undefined => {
      if (!ready) {
         return undefined;
      }
      switch (tool) {
         case EditorTool.RectangleSelect:
            return rectangleSelectHandlers;
         case EditorTool.MagicWand:
            return {
               onPointerDown: event => imageDocument.selectWithWand(
                  event.point,
                  selectionModeFromModifiers(event.shiftKey, event.altKey)
               )
            };
         case EditorTool.EraseBrush:
            return {
               onPointerDown: event => imageDocument.beginStroke(event.point, brushSize),
               onPointerMove: event => imageDocument.extendStroke(event.point),
               onPointerUp: () => imageDocument.endStroke()
            };
         default:
            return undefined;
      }
   };

   /** Renders a short white hint for the options strip. */
   const renderHint = (text: string) => (
      <Typography variant={"caption"} sx={{color: 'white', whiteSpace: 'nowrap'}}>{text}</Typography>
   );

   /**
    * Renders one white slider for the options strip, with its value as a caption. The slider's own
    * floating value label is off because it would cover, and block clicks on, the toolbar buttons above.
    */
   const renderSlider = (
      label: string,
      value: number,
      range: { min: number, max: number, step: number },
      onChange: (value: number) => void,
      formatValue: (value: number) => string = String
   ) => (
      <>
         {renderHint(`${label}: ${formatValue(value)}`)}
         <Box sx={{width: '40%'}}>
            <Slider
               aria-label={label}
               sx={{color: 'white'}}
               valueLabelDisplay={"off"}
               size={"small"}
               step={range.step}
               min={range.min}
               max={range.max}
               value={value}
               onChange={(event, newValue) => onChange(newValue as number)}
            />
         </Box>
      </>
   );

   /** Renders the slider for a brightness or contrast preview inside the selection. */
   const renderAdjustmentSlider = (kind: AdjustmentKind, label: string) => {
      const pending = imageDocument.getPendingAdjustment();
      return renderSlider(
         label,
         pending?.kind === kind ? pending.amount : 0,
         {min: -1, max: 1, step: 0.01},
         value => imageDocument.setPendingAdjustment(kind, value)
      );
   };

   /** Renders the active tool's options for the strip above the image. */
   const renderToolOptions = () => {
      if (loadFailed) {
         return renderHint('This image could not be loaded for editing.');
      }
      switch (tool) {
         case EditorTool.RectangleSelect:
            return renderHint(SELECTION_HINT);
         case EditorTool.MagicWand:
            return <>
               {renderSlider('Tolerance', imageDocument.getTolerance(), {min: 0, max: 255, step: 1},
                  value => imageDocument.setTolerance(value))}
               {renderHint(SELECTION_HINT)}
            </>;
         case EditorTool.EraseBrush:
            return renderSlider('Brush size', brushSize, {min: 1, max: 20, step: 0.5}, setBrushSize,
               value => `${value}%`);
         case EditorTool.Brightness:
            return renderAdjustmentSlider(AdjustmentKind.Brightness, 'Brightness');
         case EditorTool.Contrast:
            return renderAdjustmentSlider(AdjustmentKind.Contrast, 'Contrast');
         default:
            return null;
      }
   };

   const cropperEnabled = tool === EditorTool.Crop;
   const pointerHandlers = getPointerHandlers();
   const isSelectionTool = tool === EditorTool.RectangleSelect || tool === EditorTool.MagicWand;
   const backgroundProps: DocumentViewProps = {
      pixels: imageDocument.getPixels(),
      pixelsVersion: imageDocument.getPixelsVersion(),
      selection: imageDocument.getSelection(),
      overlayStyle: isSelectionTool ? SelectionOverlayStyle.TintAndOutline : SelectionOverlayStyle.Outline,
      pointerHandlers,
      onImageLoad: loadImage,
      onImageError: failLoad
   };

   return (
      <Box>
         <AppBar position={"static"} color={"default"} variant={"outlined"}>
            <Container maxWidth="xl">
               <Toolbar disableGutters variant={"dense"}>
                  <IconButton onClick={() => rotate(-90)} title={"Rotate left"}>
                     <RotateLeft/>
                  </IconButton>
                  <IconButton onClick={() => rotate(90)} title={"Rotate right"}>
                     <RotateRight/>
                  </IconButton>
                  <ToggleButtonGroup
                     value={tool}
                     exclusive
                     size={"small"}
                     onChange={(event, value: EditorTool | null) => value && changeTool(value)}
                  >
                     {TOOL_BUTTONS.map(button => (
                        <ToggleButton
                           key={button.tool}
                           value={button.tool}
                           title={button.title}
                           disabled={button.tool !== EditorTool.Crop && !ready}
                        >
                           {button.icon}
                        </ToggleButton>
                     ))}
                  </ToggleButtonGroup>
                  <IconButton title={"Erase selection (Delete)"} disabled={!selectionCommandsEnabled}
                              onClick={() => imageDocument.eraseSelection()}>
                     <DeleteSweep/>
                  </IconButton>
                  <IconButton title={"Deselect (Ctrl+D)"} disabled={!selectionCommandsEnabled}
                              onClick={() => imageDocument.deselect()}>
                     <Deselect/>
                  </IconButton>
                  <IconButton title={"Invert selection"} disabled={!selectionCommandsEnabled}
                              onClick={() => imageDocument.invertSelection()}>
                     <FlipToBack/>
                  </IconButton>
                  <IconButton title={"Crop to selection"} disabled={!selectionCommandsEnabled}
                              onClick={cropToSelection}>
                     <CropFree/>
                  </IconButton>
                  <IconButton title={"Undo (Ctrl+Z)"} disabled={!ready || !imageDocument.canUndo()}
                              onClick={() => imageDocument.undo()}>
                     <Undo/>
                  </IconButton>
                  <Box sx={{marginLeft: 0, width: '100%', textAlign: 'right'}}>
                     <Button variant={"contained"} onClick={onImageSave} disabled={!ready}>
                        Update
                     </Button>
                  </Box>
               </Toolbar>
            </Container>
         </AppBar>
         <Box sx={{position: 'relative'}}>
            <Cropper
               ref={cropperRef}
               style={{
                  objectFit: 'contain', maxWidth: '100%', height: '80vh',
                  cursor: pointerHandlers ? 'crosshair' : undefined
               }}
               src={src}
               stencilProps={{
                  movable: cropperEnabled,
                  resizable: cropperEnabled,
                  lines: cropperEnabled,
                  handlers: cropperEnabled,
               }}
               backgroundWrapperProps={{
                  scaleImage: true,
                  moveImage: cropperEnabled,
               }}
               defaultSize={defaultSize}
               onError={failLoad}
               backgroundComponent={AdjustableCropperBackground}
               backgroundProps={backgroundProps}
            />
            {(tool !== EditorTool.Crop || loadFailed) && (
               <Box sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  height: '30px',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                  paddingX: 2
               }}>
                  {renderToolOptions()}
               </Box>
            )}
         </Box>
      </Box>
   );
};
